import React, { useState } from 'react'
import { Modal, Button, Alert, Spinner, Badge, Form } from 'react-bootstrap'
import {
  PnsMacMiniUser,
  PnsMacMiniUserStatus,
  WispStatus,
} from 'interfaces/pns-mac-mini-user'
import { PnsMacMiniStatus } from 'interfaces/pns-mac-mini'
import useUpdateMacMiniUserFull from 'hooks/use-update-mac-mini-user-full'
import useUpdateMacMiniStatus from 'hooks/use-update-mac-mini-status'

interface MacMiniUserActivationModalProps {
  show: boolean
  onHide: () => void
  macMiniUser: PnsMacMiniUser
  onSuccess: () => void
  onCancel: () => void
}

const MacMiniUserActivationModal: React.FC<MacMiniUserActivationModalProps> = ({
  show,
  onHide,
  macMiniUser,
  onSuccess,
  onCancel,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [wispPort, setWispPort] = useState(
    macMiniUser.wispPort?.toString() || '8001'
  )

  const { mutate: updateMacMiniUser, isLoading: isUpdatingUser } =
    useUpdateMacMiniUserFull()
  const { mutate: updateMacMiniStatus, isLoading: isUpdatingMacMini } =
    useUpdateMacMiniStatus()

  const isLoading = isUpdatingUser || isUpdatingMacMini

  const handleConfirmActivation = async () => {
    try {
      const portNumber = parseInt(wispPort, 10)
      if (Number.isNaN(portNumber) || portNumber < 8000 || portNumber > 9000) {
        alert('Please enter a valid WISP port between 8000 and 9000')
        return
      }

      // Step 1: Update Mac Mini User to ACTIVE with WISP status RUNNING
      updateMacMiniUser(
        {
          userId: macMiniUser.id,
          status: PnsMacMiniUserStatus.ACTIVE,
          wispStatus: WispStatus.RUNNING,
          wispPort: portNumber,
        },
        {
          onSuccess: () => {
            // Step 2: Check if Mac Mini needs to be updated to ACTIVE
            if (macMiniUser.macMini?.status !== PnsMacMiniStatus.ACTIVE) {
              updateMacMiniStatus(
                {
                  macMiniId: macMiniUser.macMini!.id,
                  status: PnsMacMiniStatus.ACTIVE,
                },
                {
                  onSuccess: () => {
                    onSuccess()
                  },
                  onError: (error) => {
                    console.error('Failed to update Mac Mini status:', error)
                    // Still call onSuccess since user was updated successfully
                    onSuccess()
                  },
                }
              )
            } else {
              // Mac Mini already active, just complete
              onSuccess()
            }
          },
          onError: (error) => {
            console.error('Failed to update Mac Mini User:', error)
          },
        }
      )
    } catch (error) {
      console.error('Activation failed:', error)
    }
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case PnsMacMiniUserStatus.AVAILABLE:
        return 'info'
      case PnsMacMiniUserStatus.RESERVED:
        return 'warning'
      case PnsMacMiniUserStatus.ICLOUD_SETUP_IN_PROGRESS:
        return 'info'
      case PnsMacMiniUserStatus.ACTIVE:
        return 'success'
      case PnsMacMiniUserStatus.INACTIVE:
        return 'secondary'
      case PnsMacMiniUserStatus.TERMINATED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getWispStatusVariant = (status: string) => {
    switch (status) {
      case WispStatus.RUNNING:
        return 'success'
      case WispStatus.STOPPED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getStatusLabel = (status: string) => {
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')
  }

  const macMiniNeedsUpdate =
    macMiniUser.macMini?.status !== PnsMacMiniStatus.ACTIVE

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Activate Mac Mini User</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Status Transition Info */}
        <Alert variant="success" className="mb-4">
          <div className="fw-bold mb-2">User: {macMiniUser.username}</div>
          <div className="mb-2">
            Status Transition: ICLOUD_SETUP_IN_PROGRESS → ACTIVE
          </div>
          <div className="mb-2">WISP Status: STOPPED → RUNNING</div>
          {macMiniNeedsUpdate && (
            <div>Mac Mini Status: {macMiniUser.macMini?.status} → ACTIVE</div>
          )}
        </Alert>

        {/* User Details Section */}
        <h5 className="mb-3">Mac Mini User To Be Activated</h5>

        <div
          className="border border-secondary rounded p-3 mb-3"
          style={{ backgroundColor: '#2c3034' }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 text-light">
              User 1{' '}
              <Badge
                variant={getStatusVariant(macMiniUser.status)}
                className="ms-2"
              >
                {macMiniUser.status}
              </Badge>
            </h6>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
              disabled={isLoading}
            >
              {isEditing ? 'Save' : 'Edit'}
            </Button>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-2">
                <strong className="text-light">Username:</strong>{' '}
                <span className="text-light">{macMiniUser.username}</span>
              </div>
              <div className="mb-2">
                <strong className="text-light">Current Status:</strong>{' '}
                <Badge variant={getStatusVariant(macMiniUser.status)}>
                  {getStatusLabel(macMiniUser.status)}
                </Badge>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <strong className="text-light">New Status:</strong>{' '}
                <Badge variant={getStatusVariant(PnsMacMiniUserStatus.ACTIVE)}>
                  Active
                </Badge>
              </div>
              <div className="mb-2">
                <strong className="text-light">WISP Status:</strong>{' '}
                <Badge variant={getWispStatusVariant(WispStatus.RUNNING)}>
                  Running
                </Badge>
              </div>
            </div>
          </div>

          {/* WISP Port Section */}
          <div className="row mt-2">
            <div className="col-md-6">
              <div className="mb-2">
                <strong className="text-light">WISP Port:</strong>{' '}
                {isEditing ? (
                  <Form.Control
                    type="number"
                    size="sm"
                    value={wispPort}
                    onChange={(e) => setWispPort(e.target.value)}
                    min="8000"
                    max="9000"
                    className="d-inline-block"
                    style={{ width: '100px', marginLeft: '8px' }}
                  />
                ) : (
                  <span className="text-light">{wispPort}</span>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <strong className="text-light">Mac Mini:</strong>{' '}
                <span className="text-light">
                  {macMiniUser.macMini?.name || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mac Mini Update Section */}
        {macMiniNeedsUpdate && (
          <Alert variant="warning">
            <strong>Note:</strong> The Mac Mini "{macMiniUser.macMini?.name}"
            will also be updated to ACTIVE status since this user is becoming
            active.
          </Alert>
        )}

        <Alert variant="info">
          <strong>Action:</strong> This will activate the Mac Mini user and
          start the WISP service. The user will be ready for active use.
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleConfirmActivation}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Activating...
            </>
          ) : (
            'Activate User & Start WISP'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MacMiniUserActivationModal
